import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Bell, Compass, Check, Sparkles, Radar } from "lucide-react";
import SearchBar from "../components/SearchBar";
import FlightCard from "../components/FlightCard";
import Seo from "../components/Seo";
import Faq, { faqJsonLd } from "../components/Faq";
import TrendingRoutes from "../components/TrendingRoutes";
import { Button } from "../components/ui/button";
import { exploreDeals, REGIONS, FEATURES, HOME_FAQ, isDateLocked } from "../data/mock";
import { useIsPro } from "../components/ProGate";

const badgeStyle = (b) =>
  b === "PRO" ? "bg-blue-600 text-white" : b === "New" ? "bg-purple-100 text-purple-700" : "bg-green-100 text-green-700";
const featIcon = { "Continuous Alerts": Bell, "Lightning Fast Search": Zap, "Explore Awards": Compass, "Live Search": Zap, "AI Assistant": Sparkles, "Multi-city codes": Compass };

export default function Home() {
  const [region, setRegion] = useState("North America");
  const isPro = useIsPro();
  const deals = useMemo(() => exploreDeals(region, "business", 24).filter((d) => !isDateLocked(d.date, isPro)).slice(0, 12), [region, isPro]);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "FindMyFlight",
      url: window.location.origin,
      logo: `${window.location.origin}/favicon.ico`,
      description: "FindMyFlight is an award flight search engine that finds the best flights for your points and miles across 19 airline loyalty programs.",
      sameAs: [],
    },
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "FindMyFlight",
      applicationCategory: "TravelApplication",
      operatingSystem: "Web",
      url: window.location.origin,
      description: "The fastest search engine for award travel. Search award availability, explore regions, compare cabins, set price-drop alerts and plan trips with an AI assistant.",
      featureList: FEATURES.map((f) => f.title).join(", "),
      offers: [
        { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD", description: "Search, Explore, Award Calendar and wishlist for the next 30 days." },
        { "@type": "Offer", name: "PRO", price: "8.99", priceCurrency: "USD", description: "Up to a year of availability, live search and advanced filters.", priceSpecification: { "@type": "UnitPriceSpecification", price: "8.99", priceCurrency: "USD", billingDuration: "P1M" } },
      ],
    },
    faqJsonLd(HOME_FAQ),
  ];

  return (
    <main>
      <Seo
        title="FindMyFlight — The fastest search engine for your points and miles"
        description="Discover award availability on popular airlines and mileage programs. Search with instant results, explore entire regions, compare cabins and track flights live."
        path="/"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="hero-grid relative overflow-hidden border-b border-slate-100">
        <div className="mx-auto max-w-4xl px-5 pb-16 pt-16 text-center md:pt-20">
          <h1 className="fade-up text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 md:text-6xl">
            Discover the best flights<br className="hidden md:block" /> for your points.
          </h1>
          <p className="fade-up mx-auto mt-5 max-w-2xl text-base text-slate-600 md:text-lg">
            FindMyFlight is the fastest search engine for award travel. Explore availability across entire regions, search with instant results, watch your flight live and more.
          </p>
          <div className="fade-up mx-auto mt-8 max-w-3xl text-left">
            <SearchBar defaultTab="search" />
          </div>
          <Link to="/tracker" data-testid="home-tracker-banner" className="mx-auto mt-6 flex max-w-md items-center justify-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm text-blue-800 transition-colors hover:bg-blue-100">
            <Radar className="h-4 w-4" /> <span className="font-semibold">New!</span> Live Flight Tracker — watch any flight in real time (PRO) <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>

      {/* Explore deals */}
      <TrendingRoutes />
      <section className="mx-auto max-w-7xl px-5 py-16">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <h2 className="text-2xl font-bold text-slate-900 md:text-3xl">
            Explore deals from <span className="text-blue-600">{region}</span>
          </h2>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium outline-none focus:border-blue-500"
          >
            {REGIONS.map((r) => <option key={r}>{r}</option>)}
          </select>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {deals.map((d) => <FlightCard key={d.id} d={d} />)}
        </div>
        <div className="mt-8 text-center">
          <Link to="/explore"><Button variant="outline" className="gap-2">See all deals <ArrowRight className="h-4 w-4" /></Button></Link>
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-50 py-16">
        <div className="mx-auto max-w-7xl px-5">
          <h2 className="text-center text-2xl font-bold text-slate-900 md:text-3xl">Everything you need to fly on points</h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-slate-600">Powerful, fast, and free forever for the essentials.</p>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => {
              const Icon = featIcon[f.title] || Zap;
              return (
                <div key={f.title} className="deal-card rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Icon className="h-5 w-5" /></span>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${badgeStyle(f.badge)}`}>{f.badge}</span>
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-slate-900">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.body}</p>
                  <Link to={f.to} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:gap-2 transition-all">{f.cta} <ArrowRight className="h-3.5 w-3.5" /></Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Free forever / PRO */}
      <section className="mx-auto max-w-7xl px-5 py-16">
        <div className="grid items-center gap-8 rounded-3xl border border-slate-200 bg-gradient-to-br from-blue-50 to-white p-8 md:grid-cols-2 md:p-12">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 md:text-3xl">FindMyFlight is free, forever</h2>
            <p className="mt-4 text-slate-600">Search, explore availability, and create alerts for flights departing within the next two months for free — no registration required. When you’re ready, a PRO account unlocks up to a year of availability, advanced filters, SMS alerts, and more — $8.99/mo, or pay yearly and get 2 months free.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/pricing"><Button className="bg-blue-600 font-semibold hover:bg-blue-700">Go PRO — $8.99/mo or $89.90/yr</Button></Link>
              <Link to="/search"><Button variant="outline">Run a free search</Button></Link>
            </div>
          </div>
          <ul className="space-y-3">
            {["Up to a full year of availability", "Advanced filters: max fees, exclude carriers", "SMS & Discord alerts", "Live real-time search on any route", "Interactive seat maps (GDS access)"].map((li) => (
              <li key={li} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white px-4 py-3 text-sm font-medium text-slate-700">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white"><Check className="h-3 w-3" /></span>
                {li}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Press */}
      {/* FAQ (answer-engine friendly) */}
      <div className="border-t border-slate-100 bg-slate-50">
        <Faq items={HOME_FAQ} title="FindMyFlight, explained" subtitle="Straight answers to the questions travelers ask most about searching award flights with points and miles." />
      </div>

    </main>
  );
}
